import {
  ArrayField,
  BooleanInput,
  BulkDeleteButton,
  BulkExportButton,
  ChipField,
  Datagrid,
  DateField,
  EditButton,
  List,
  SelectInput,
  SingleFieldList,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  usePermissions,
  useUnselect,
} from 'react-admin'

import { Box, CircularProgress, Dialog, DialogContent } from '@mui/material'
import { FolderArrowDownIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import authProvider from '../authProvider'
import AmountField from '../components/AmountField'
import { Title } from '../components/Title'
import MigrateButton from './MigrateButton'
import VendorMatchFlags from './VendorMatchFlags'
import ReferenceVendorMapper from './ReferenceVendorMapper'
import AssignGroupButton from './AssignGroupButton'
import userPermissions from '../Users/userPermissions'
import { getLabelSuffix } from '../helpers'
import { DateRangeFilterContext } from '../contexts/DateRangeFilterContext'
import { useQueryFilter } from '../helpers/useQueryFilter'
import { Button } from '../components/common/Button'
import TransactionListBulkEdit from './TransactionListBulkEdit'
import { TransactionListInitialQuery } from './TransactionList.types'

export const ops = [
  { name: 'in', label: 'in' },
  { name: 'notin', label: 'not in' },
]

export const vendorOps = [
  { name: 'in', label: 'in' },
  { name: 'notin', label: 'not in' },
]

const ExportButton = () => {
  const { filterValues } = useListContext()
  const dateRangeFilter = useContext(DateRangeFilterContext)
  const { currentQuery } = useQueryFilter()
  const [format, setFormat] = useState('excel')
  const [isGenerating, setIsGenerating] = useState(false)
  const dp = useDataProvider()

  // Create a complete filter object that includes date range parameters
  const completeFilterValues = {
    ...filterValues,
    startDate: dateRangeFilter.ranges?.[0]?.startDate,
    endDate: dateRangeFilter.ranges?.[0]?.endDate,
    query: filterValues.query || JSON.stringify(currentQuery),
  }

  const downloadFile = (token) => {
    setIsGenerating(true)
    dp.getCSVUrl(completeFilterValues, token, format)
      .then((url) => {
        // Get the authentication token
        authProvider
          .getAccessToken()
          .then((authToken) => {
            // Use fetch to make the API call directly
            fetch(url, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            })
              .then((response) => {
                // When the response is received, the API call is complete
                // Create a blob from the response
                return response.blob()
              })
              .then((blob) => {
                // Create a download link and trigger it
                const downloadUrl = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = downloadUrl

                // Generate filename with seconds included
                a.download = `spendqube_export_${new Date()
                  .toISOString()
                  .replace(/[:.]/g, '')
                  .replace('T', '_')
                  .slice(0, 17)}.${format === 'excel' ? 'xlsx' : 'csv'}`
                document.body.appendChild(a)
                a.click()
                window.URL.revokeObjectURL(downloadUrl)
                document.body.removeChild(a)

                // Close the modal immediately after the download starts
                setIsGenerating(false)
              })
              .catch(() => {
                setIsGenerating(false) // Hide modal if there's an error
              })
          })
          .catch(() => {
            setIsGenerating(false) // Hide modal if there's an error with getting the token
          })
      })
      .catch(() => {
        setIsGenerating(false) // Hide modal if there's an error with getting the URL
      })
  }

  const handleDownload = () => {
    setIsGenerating(true)
    dp.getCSVRowCount(completeFilterValues)
      .then((data) => {
        // magic number for rows
        const confirmLimit = 1048576
        if (data.data.count >= confirmLimit) {
          setIsGenerating(false) // Hide modal while showing confirm dialog
          if (
            window.confirm(
              'This export will exceed 1,048,576 rows. Are you sure you wish to continue?'
            )
          ) {
            setIsGenerating(true) // Show modal again if user confirms
            downloadFile(data.data.token)
          }
        } else {
          downloadFile(data.data.token)
        }
      })
      .catch(() => {
        setIsGenerating(false) // Hide modal if there's an error
      })
  }

  return (
    <>
      <div className="flex items-center space-x-2">
        <select
          className="py-2 px-3 border border-gray-300 rounded text-sm"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        >
          <option value="excel">Excel</option>
          <option value="csv">CSV</option>
        </select>
        <button
          type="button"
          className="py-2 border border-green-800 p-3 active rounded bg-green-700 text-white font-medium text-sm inline-flex space-x-2 items-center hover:bg-opacity-90"
          color="primary"
          onClick={handleDownload}
        >
          <FolderArrowDownIcon className="h-4" />
          <span>Download</span>
        </button>
      </div>

      {/* Loading Modal */}
      <Dialog
        open={isGenerating}
        disableEscapeKeyDown
        hideBackdrop={false}
        onClose={(event, reason) => {
          // Prevent closing the dialog by clicking outside
          if (reason !== 'backdropClick') {
            setIsGenerating(false)
          }
        }}
      >
        <DialogContent
          sx={{ width: '400px', textAlign: 'center', padding: '24px' }}
        >
          <CircularProgress sx={{ marginBottom: '16px' }} />
          <div className="text-lg font-medium mb-4">
            Generating Report... Please Wait
          </div>
          <div className="text-sm text-gray-600">
            This may take a few moments for large datasets.
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const BulkActionList = () => {
  const { permissions } = usePermissions()
  const { selectedIds } = useListContext()

  if (selectedIds?.length < 1) return null
  if (!(permissions & userPermissions.EDIT_TRANSACTIONS)) return null

  return (
    <div className="relative">
      <div>
        <div className="bg-gray-100 text-xs font-medium py-1.5 px-3 rounded-t border border-b-0 inline-block">
          Bulk edit ({selectedIds.length} Selected)
        </div>
      </div>
      <div className="flex items-center gap-2.5 fade-in-up py-1 px-2 rounded-md rounded-tl-none border bg-white">
        <BulkDeleteButton mutationMode="pessimistic" />
        <BulkExportButton />
        {/* <ConfirmVendorButton /> */}
      </div>
    </div>
  )
}

const ListActions = ({
  setOpenBulkEditModal,
}: {
  setOpenBulkEditModal: Dispatch<SetStateAction<boolean>>
}) => {
  const { permissions } = usePermissions()
  const list = useListContext()
  const location = useLocation()
  const unselect = useUnselect(list.resource)

  useEffect(() => {
    unselect(list.selectedIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  return (
    <Box width="100%">
      <TopToolbar>
        <BulkActionList />
        {permissions & userPermissions.EDIT_TRANSACTIONS && (
          <Button
            disabled={!list.data?.length}
            onClick={() => setOpenBulkEditModal((prev) => !prev)}
          >
            Edit {list.selectedIds?.length ? 'Selected' : 'All'}
          </Button>
        )}
        <ExportButton />
      </TopToolbar>
    </Box>
  )
}

const TransactionList = () => {
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false)
  const { permissions } = usePermissions()
  const { t } = useTranslation()
  const suffix = getLabelSuffix()

  const dateRangeFilter = useContext(DateRangeFilterContext)
  const { currentQuery } = useQueryFilter()

  const TransactionFilters = [
    <SelectInput
      sx={{ minWidth: 300 }}
      label={t(`vendor_match_flags${suffix}`)}
      source="vendor_match_flags"
      choices={VendorMatchFlags}
      emptyText="Show all"
      alwaysOn
    />,
    <BooleanInput
      label={t(`uncategorised_transactions${suffix}`)}
      source="untagged"
      alwaysOn
      parse={Boolean}
    />,
  ]

  const initialQuery: TransactionListInitialQuery = {
    startDate: dateRangeFilter.ranges?.[0]?.startDate,
    endDate: dateRangeFilter.ranges?.[0]?.endDate,
    query: JSON.stringify(currentQuery),
  }

  // Reset pagination when external filters (date range or query) change by forcing List remount
  const filterSignature = JSON.stringify({
    startDate: initialQuery.startDate
      ? new Date(initialQuery.startDate as any).toISOString()
      : null,
    endDate: initialQuery.endDate
      ? new Date(initialQuery.endDate as any).toISOString()
      : null,
    query: initialQuery.query,
  })

  return (
    <section className="space-y-6 w-full">
      <Title>{t(`transactions${suffix}`)}</Title>
      <List
        key={filterSignature}
        className="w-full"
        sort={{ field: 'invoice_date', order: 'DESC' }}
        actions={<ListActions setOpenBulkEditModal={setOpenBulkEditModal} />}
        filters={TransactionFilters}
        filter={initialQuery}
        sx={{ RaFilterForm: { alignSelf: 'center' } }}
      >
        <Datagrid className="w-full">
          <TextField source="txn_id" label="Tx Number" />
          <TextField source="invoice_vendor_name" label="Vendor" />
          <AmountField label="Amount" />
          <DateField source="invoice_date" />
          <ArrayField source="tags" sortable={false}>
            <SingleFieldList className="flex" linkType={false}>
              <ChipField
                source="name"
                className="!max-w-[400px] text-ellipsis"
              />
            </SingleFieldList>
          </ArrayField>
          {permissions & userPermissions.SUPER_ADMIN && (
            <ReferenceVendorMapper />
          )}
          {permissions & userPermissions.EDIT_TRANSACTIONS && (
            <AssignGroupButton />
          )}
          {permissions & userPermissions.EDIT_TRANSACTIONS && <MigrateButton />}
          {permissions & userPermissions.EDIT_TRANSACTIONS && (
            <EditButton label="" />
          )}
        </Datagrid>
        {openBulkEditModal && (
          <TransactionListBulkEdit
            open={openBulkEditModal}
            onClose={() => setOpenBulkEditModal(false)}
            initialQuery={initialQuery}
          />
        )}
      </List>
    </section>
  )
}

export default TransactionList
